<!-- @format -->
<template>
    <div class="previously-logged-activity-widget">
        <div class="widget-header">
            <div class="title-container">
                <h3>Add Previously Logged Activities</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>

        <div class="content-wrapper">
            <div class="activity-form-row">
                <div class="input-wrapper contact" :class="contactClass">
                    <p>Select a contact to associate their activities with this opportunity.</p>
                    <label>Select a Contact</label>
                    <contact-search
                        v-model="contact"
                        :client-id="clientId"
                        :disabled="isContactDropDownDisabled"
                        :filtering-contact-ids="filteringContactIds"
                    />
                    <p v-if="!isValidContact" class="error helper-text">Required</p>
                </div>
            </div>
            <template v-if="contact">
                <div v-if="selectedActivities.length" class="outlined-section">
                    <div class="section-title">Selected Activities</div>
                    <div class="outlined-section-content-container">
                        <div
                            v-for="(selectedActivity, activityIndex) in selectedActivities"
                            :key="selectedActivity.vueKey"
                        >
                            <div class="activity-container">
                                <div class="activity-btn-profile">
                                    <div class="add-activity-btn-container">
                                        <button
                                            :class="['btn', 'add-activity-btn']"
                                            @click="handleDeselectActivity(selectedActivity.vueKey)"
                                        >
                                            <l24-checkmark-svg />
                                        </button>
                                    </div>
                                    <div
                                        class="image-container"
                                        :style="{
                                            background: `url('${selectedActivity.activities[0].actor.profileImageUrl}')`,
                                        }"
                                    >
                                        <img
                                            class="icon-image"
                                            :src="selectedActivity.iconUrl"
                                            alt=""
                                        />
                                    </div>
                                </div>
                                <div class="content-container">
                                    <div class="details-action-container">
                                        <div class="details-container">
                                            <label>{{ selectedActivity.type }}</label>
                                            <div class="date-container">
                                                {{
                                                    selectedActivity.activities[0].date.format(
                                                        'ddd | MMM DD, YYYY',
                                                    )
                                                }}
                                            </div>
                                        </div>
                                        <div class="details-actions">
                                            <button
                                                v-if="selectedActivity.hasPreview"
                                                class="preview-action-container"
                                                @click="handleOpenPreview(selectedActivity)"
                                            >
                                                <d24-show-hide-outline-svg />
                                            </button>
                                            <app-button
                                                class="remove-button"
                                                @click.native.stop="
                                                    handleDeselectActivity(selectedActivity.vueKey)
                                                "
                                            >
                                                Remove <s4-delete-svg />
                                            </app-button>
                                        </div>
                                    </div>
                                    <div v-html="selectedActivity.activities[0].content" />
                                </div>
                            </div>
                            <div
                                v-if="activityIndex + 1 !== selectedActivities.length"
                                class="divider"
                            />
                        </div>
                    </div>
                </div>
                <p :class="selectActivitiesClass">
                    Please select the activities that you would like to associate with this
                    opportunity.
                </p>
                <p v-if="!isValidSelectedActivities" class="error helper-text">
                    * Make a selection
                </p>
                <div class="filter-button-container">
                    <app-search-input :value="search" @input="updateSearch" />
                    <div class="divider" />
                    <div
                        class="filter-dropdown-button-container"
                        :class="componentClasses"
                        @click.stop
                    >
                        <app-button class="filter-button" @click="toggleDropdown()">
                            <span class="icon-container">
                                <e24-filter-outline-svg />
                                <span v-show="filterCount" class="total">
                                    {{ filterCount }}
                                </span>
                            </span>
                            <span class="button-text">Filter</span>
                        </app-button>
                        <div class="filter-dropdown" :class="componentClasses">
                            <section class="title">
                                Filters
                                <app-button class="clear-all cancel-button" @click="cleanFilters"
                                    >Clear All</app-button
                                >
                            </section>
                            <section>
                                <div class="filter-dropdowns-container">
                                    <section v-show="activeFilters.length" class="active-filters">
                                        <div class="active-filters-title">
                                            <span class="section-title">Active Filters</span>
                                            <span>{{ totalItems }} Results</span>
                                        </div>
                                        <board-filter-chips
                                            :filters="activeFilters"
                                            @filter:remove="removeActiveFilter"
                                        />
                                    </section>
                                    <section class="quick-filters">
                                        <section>
                                            <span class="section-title"
                                                >Filter by Activity Type</span
                                            >
                                            <general-quick-filter-dropdown
                                                :items="activityTypeOptions"
                                                @input="updateActivityType"
                                            ></general-quick-filter-dropdown>
                                        </section>
                                    </section>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
                <div class="available-activities-container">
                    <div
                        v-for="(availableActivity, activityIndex) in filteredActivities"
                        :key="availableActivity.vueKey"
                    >
                        <div class="activity-container">
                            <div class="activity-btn-profile">
                                <div class="add-activity-btn-container">
                                    <button
                                        :class="['btn', 'add-activity-btn']"
                                        @click="handleSelectActivity(availableActivity.vueKey)"
                                    >
                                        <o6-add-svg color="#ffffff" />
                                    </button>
                                </div>
                                <div
                                    class="image-container"
                                    :style="{
                                        background: `url('${availableActivity.activities[0].actor.profileImageUrl}')`,
                                    }"
                                >
                                    <img
                                        class="icon-image"
                                        :src="availableActivity.iconUrl"
                                        alt=""
                                    />
                                </div>
                            </div>
                            <div class="content-container">
                                <div class="details-action-container">
                                    <div class="details-container">
                                        <label>{{ availableActivity.type }}</label>
                                        <div class="date-container">
                                            {{
                                                availableActivity.activities[0].date.format(
                                                    'ddd | MMM DD, YYYY',
                                                )
                                            }}
                                        </div>
                                    </div>
                                    <div class="details-actions">
                                        <button
                                            v-if="availableActivity.hasPreview"
                                            class="preview-action-container"
                                            @click="handleOpenPreview(availableActivity)"
                                        >
                                            <d24-show-hide-outline-svg />
                                        </button>
                                    </div>
                                </div>
                                <div v-html="availableActivity.activities[0].content" />
                            </div>
                        </div>
                        <div v-if="activityIndex + 1 !== activities.length" class="divider" />
                    </div>
                    <div
                        v-if="!activities.length && !isActivitiesLoading"
                        class="no-activities-container"
                    >
                        <div class="svg-container">
                            <img src="@/assets/images/SVG/no-records-svg.svg" alt="No Activities" />
                            <h4 v-if="filteredActivityType !== 'All'">
                                No {{ filteredActivityType.toLowerCase() }} yet!
                            </h4>
                            <h4 v-else>No activities yet!</h4>
                        </div>
                    </div>
                </div>
                <div v-show="!areAllActivitiesFetched" class="btn-container show-more-btn">
                    <app-button
                        :disabled="isActivitiesLoading"
                        :loading="isActivitiesLoading"
                        @click="
                            showMoreRecords({
                                contactId: contact.id,
                                pageSize,
                                type: filteredActivityType,
                                search,
                            })
                        "
                    >
                        Show More Activities
                    </app-button>
                </div>
            </template>
        </div>

        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button
                    :disabled="!selectedActivities.length && !activities.length"
                    :loading="isActivitiesLoading || isSaving"
                    @click="handleSave"
                >
                    Save
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const ContactSearch = () => import('@/components/contact/contact-search');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const AppSearchInput = () => import('@/components/common/app-search-input');
const E24FilterOutlineSvg = () => import('@/components/svg/grid/e24-filter-outline-svg');
const BoardFilterChips = () =>
    import('@/components/design-studio/boards/filters/board-filter-chips');
const GeneralDropdown = () =>
    import('@/components/design-studio/boards/filters/general-quick-filter-dropdown');
const O6AddSvg = () => import('@/components/svg/grid/o6-add-svg');
const L24CheckmarkSvg = () => import('@/components/svg/grid/l24-checkmark-svg');
const S4DeleteSvg = () => import('@/components/svg/grid/s4-delete-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const D24ShowHideOutlineSvg = () => import('@/components/svg/grid/d24-show-hide-outline-svg');

import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';
import ActivityEmailPreviewPopup from '@/components/popups/preview/activity-email-preview-popup';
import { ActivityServiceMixin } from '@/mixins/activity-mixin';

export default {
    name: 'previously-logged-activity-widget',

    components: {
        'app-button': AppButton,
        'contact-search': ContactSearch,
        'widget-close-svg': WidgetCloseSvg,
        'app-search-input': AppSearchInput,
        'e24-filter-outline-svg': E24FilterOutlineSvg,
        'board-filter-chips': BoardFilterChips,
        'general-quick-filter-dropdown': GeneralDropdown,
        'o6-add-svg': O6AddSvg,
        'l24-checkmark-svg': L24CheckmarkSvg,
        's4-delete-svg': S4DeleteSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'd24-show-hide-outline-svg': D24ShowHideOutlineSvg,
    },

    mixins: [ActivityServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },

        options: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            isActivitiesLoading: false,
            isSaving: false,
            isContactDropDownDisabled: false,
            clientId: this.options.clientId,
            filteringContactIds: this.options.contactIds,
            dealId: this.options.dealId,

            contact: null,

            clickedSave: false,
            search: '',
            activeFilters: [],
            totalItems: 0,
            filteredActivityType: 'All',
            activityTypeOptions: [
                { name: 'Calls' },
                { name: 'Follow Up Emails' },
                { name: 'Contact Emails' },
                { name: 'Opened Emails' },
                { name: 'Clicked Emails' },
                { name: 'SMS' },
                { name: 'Notes' },
                { name: 'Meetings' },
                { name: 'Form Submissions' },
                { name: 'Unsubscribes' },
            ],

            openDropdown: false,
            selectedActivities: [],
            activities: [],
            areAllActivitiesFetched: true,
            pageSize: 9,
            pagesSkipped: 0,
        };
    },

    validations: {
        contact: {
            required,
        },
    },

    computed: {
        filteredActivities() {
            return this.activities.filter(
                (activity) => !this.selectedActivities.some((sa) => sa.key === activity.key),
            );
        },

        isValidSelectedActivities() {
            return !this.clickedSave || !!this.selectedActivities.length;
        },

        selectActivitiesClass() {
            return !this.isValidSelectedActivities ? 'activities-error' : '';
        },

        isValidContact() {
            return !this.clickedSave || this.$v.contact.required;
        },

        contactClass() {
            return !this.isValidContact ? 'error' : '';
        },

        componentClasses() {
            return [{ opened: this.openDropdown }];
        },

        filterCount() {
            return this.activeFilters.length;
        },

        skippedRecords() {
            return this.pageSize * this.pagesSkipped;
        },
    },

    watch: {
        contact() {
            this.clickedSave = false;
            this.activeFilters = [];
            if (this.contact) {
                this.activities = [];
                this.pagesSkipped = 0;
                if (this.filteredActivityType === 'All' && !this.search) {
                    this.getActivities({
                        contactId: this.contact.id,
                        pageSize: this.pageSize,
                        skippedRecords: this.skippedRecords,
                        type: this.filteredActivityType,
                    });
                }
                this.search = '';
                this.filteredActivityType = 'All';
            }
        },

        filteredActivityType(newValue, oldValue) {
            if (newValue != oldValue) {
                this.pagesSkipped = 0;
                this.activities = [];
                this.areAllActivitiesFetched = false;
                this.getActivities({
                    contactId: this.contact.id,
                    pageSize: this.pageSize,
                    skippedRecords: this.skippedRecords,
                    type: this.filteredActivityType,
                    search: this.search,
                });
            }
        },

        search(newValue, oldValue) {
            if (newValue != oldValue) {
                this.pagesSkipped = 0;
                this.activities = [];
                this.areAllActivitiesFetched = false;
                this.getActivities({
                    contactId: this.contact.id,
                    pageSize: this.pageSize,
                    skippedRecords: this.skippedRecords,
                    type: this.filteredActivityType,
                    search: this.search,
                });
            }
        },
    },

    mounted() {
        this.addGlobalClickListener();
    },

    methods: {
        handleSave() {
            this.clickedSave = true;
            this.isSaving = true;
            const meetings = this.selectedActivities.filter(({ type }) => type === 'Meeting');
            const coreActivities = this.selectedActivities.filter(({ type }) => type !== 'Meeting');
            const promises = [];
            if (coreActivities.length) {
                const restUrl = '/api/Activities/Deal';
                const bodyData = {
                    ActivitiesId: coreActivities.map((group) => group.activities[0].id),
                    DealId: this.dealId,
                };

                if (this.isValidContact && this.isValidSelectedActivities) {
                    promises.push(
                        new Promise((resolve, reject) => {
                            this.$api
                                .odataPatch(restUrl, bodyData)
                                .then(() => {
                                    resolve();
                                })
                                .catch((error) => {
                                    this.$helpers.showFailNotification(
                                        'An error occurred while adding your activities.',
                                        { error },
                                    );
                                    reject();
                                });
                        }),
                    );
                }
            }

            if (meetings.length) {
                const meetingPromises = meetings.map(
                    (group) =>
                        new Promise((resolve, reject) => {
                            const restUrl = `/api/MeetingActivities/${group.activities[0].id}`;
                            var changes = [
                                {
                                    name: 'DealId',
                                    value: this.dealId,
                                },
                            ];

                            this.$api
                                .patch(restUrl, changes)
                                .then(() => resolve())
                                .catch((error) => {
                                    this.$helpers.showFailNotification(
                                        'An error occurred while saving your activity.',
                                        { error },
                                    );
                                    reject();
                                });
                        }),
                );

                meetingPromises.forEach((promise) => promises.push(promise));
            }

            Promise.all(promises)
                .then(() => {
                    EventBus.$emit('opportunity:update');
                    this.$helpers.showSuccessNotification(
                        'Your activities have been added to opportunity.',
                    );
                    this.closeWidget();
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        updateSearch(search) {
            this.search = search;
        },

        cleanFilters() {
            this.removeActiveFilter();
        },

        removeActiveFilter() {
            this.activeFilters = [];
            this.filteredActivityType = 'All';
        },

        updateActivityType(activityType) {
            this.activeFilters = [activityType];
            this.filteredActivityType = activityType.name;
        },

        toggleDropdown() {
            this.openDropdown = !this.openDropdown;
        },

        addGlobalClickListener() {
            document
                .getElementsByClassName('app-wrapper')[0]
                .addEventListener('click', this.closeDropdowns);
        },

        closeDropdowns() {
            this.openDropdown = false;
        },

        handleSelectActivity(vueKey) {
            const selected = this.activities.find((activity) => activity.vueKey === vueKey);
            if (selected) {
                this.selectedActivities.push(selected);
                this.activities = this.activities.filter((activity) => activity.vueKey !== vueKey);
            }
        },

        handleDeselectActivity(vueKey) {
            const selected = this.selectedActivities.find((activity) => activity.vueKey === vueKey);
            if (selected) {
                this.activities.push(selected);
                this.selectedActivities = this.selectedActivities.filter(
                    (activity) => activity.vueKey !== vueKey,
                );
            }
        },

        handleOpenPreview(activity) {
            this.$modal.show(
                ActivityEmailPreviewPopup,
                {
                    element: {
                        activity,
                    },
                },
                {
                    height: '80%',
                    width: '80%',
                },
            );
        },
    },
};
</script>

<style>
.previously-logged-activity-widget .loading-screen-container {
    display: flex;
    justify-content: center;
}

.previously-logged-activity-widget .loading-screen {
    margin: 0 auto;
}

/*Unknown Schema Requirement*/
/*Unknown Location*/
.previously-logged-activity-widget .form-group--error .error {
    font-size: 12px;
    color: #c00;
    margin-top: 2px;
}

.previously-logged-activity-widget .action-container {
    display: flex;
}

.previously-logged-activity-widget .action-container .btn {
    margin-left: 15px;
}

.previously-logged-activity-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    border: 1px solid var(--color-border-accent-silvergray);
    color: var(--color-text-default);
}

/*Unknown Schema Requirement*/
.previously-logged-activity-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

/*Unknown Location*/
.previously-logged-activity-widget .action-container .btn.white-btn {
    background: var(--color-fill-accent-white);
    color: var(--color-text-subtle);
    border: 2px solid var(--color-border-accent-brightgray);
}

.previously-logged-activity-widget .app-profile-image .background-image {
    height: 15px;
    width: 15px;
    margin-right: 8px;
}

.previously-logged-activity-widget .activity-form-row {
    display: flex;
    flex-direction: column;
}

.previously-logged-activity-widget .activity-form-row.activity-profile-image .profile-image {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    margin-right: 35px;
}

.previously-logged-activity-widget .activity-form-row .input-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 24px;
}

/*Unknown Location*/
.previously-logged-activity-widget .activity-form-row .input-wrapper.error label,
.previously-logged-activity-widget .content-wrapper .activities-error {
    color: var(--color-text-warning);
    font-weight: bold;
}

/*Unknown Location*/
.previously-logged-activity-widget .content-wrapper .error.helper-text {
    font: normal normal normal 12px/14px Roboto;
    letter-spacing: 0px;
    color: var(--color-text-warning);
}

.previously-logged-activity-widget .activity-form-row .input-wrapper.error .error.helper-text {
    margin: 8px 0 0;
}

/*Unknown Location*/
.previously-logged-activity-widget
    .activity-form-row
    .input-wrapper.error
    .app-multiselect-new.multiselect
    .multiselect__tags {
    border-color: var(--color-text-warning);
}

.previously-logged-activity-widget .filter-button-container {
    display: flex;
}

.previously-logged-activity-widget .filter-button-container .divider {
    height: 26px;
    width: 1px;
    margin: 0 18px;
    border-right: 2px solid var(--color-border-divider);
    display: block;
}

/*Unknown Schema Requirement*/
.previously-logged-activity-widget .filter-dropdown-button-container .filter-dropdown {
    display: none;
    position: absolute;
    top: 305px;
    left: 35px;
    width: 580px;
    background: var(--color-background-secondary);
    border: 2px solid var(--color-border-accent-brightgray);
    box-shadow: 0px 3px 6px #8274e833;
    border-radius: 12px;
    font-size: 12px;
    z-index: 10;
}

.previously-logged-activity-widget .filter-dropdown-button-container .filter-dropdown template {
    display: block;
}

.previously-logged-activity-widget .filter-dropdown-button-container .filter-dropdown .title {
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 12px;
    font-weight: 600;
    border-bottom: 1px solid var(--color-border-accent-silvergray);
}

.previously-logged-activity-widget
    .filter-dropdown-button-container
    .filter-dropdown
    .title
    .app-button {
    height: 35px;
    font-size: 12px;
    font-weight: 400;
}

.previously-logged-activity-widget .filter-dropdown-button-container .filter-dropdown .filters {
    padding: 18px;
}

.previously-logged-activity-widget .filter-dropdown-button-container.opened .filter-dropdown {
    display: block;
}

.previously-logged-activity-widget
    .filter-dropdown-button-container
    .filter-dropdown
    section.title
    .app-button.clear-all {
    color: var(--color-text-default);
    width: 84px;
    border: 1px solid var(--color-border-accent-silvergray);
    font: normal normal 14px/25px Roboto;
    font-weight: 500;
}

.previously-logged-activity-widget .app-button.filter-button {
    background: transparent;
    padding: 0px;
    height: 35px;
    border: 1px solid transparent;
    width: auto;
    min-width: 100px;
}

/*Unknown Schema Requirement*/
.previously-logged-activity-widget .app-button.filter-button:hover {
    background-color: #dcdbe0;
}

.previously-logged-activity-widget .app-button.filter-button .e24-filter-outline-svg {
    width: 14px;
}

.previously-logged-activity-widget .app-button.filter-button .button-text {
    display: block;
    color: var(--color-text-default);
    margin: 0 15px 0 10px;
}

.previously-logged-activity-widget .app-button.filter-button .icon-container {
    position: relative;
    display: flex;
    margin-left: 15px;
}

/*Unknown Schema Requirement*/
.previously-logged-activity-widget .app-button.filter-button .icon-container .total {
    font: normal normal bold 12px Roboto;
    text-align: left;
    letter-spacing: 0px;
    color: var(--color-text-default);
    text-shadow: 0px 3px 6px #00000029;
    position: absolute;
    bottom: -4px;
    right: -4px;
}

.previously-logged-activity-widget .filter-dropdowns-container,
.previously-logged-activity-widget .filter-dropdowns-container section {
    display: flex;
    flex-direction: column;
}

/*Unknown Location*/
.previously-logged-activity-widget .filter-dropdowns-container .active-filters {
    padding: 16px;
    border-bottom: 1px solid var(--color-border-accent-silvergray);
}

.previously-logged-activity-widget
    .filter-dropdowns-container
    .active-filters
    .active-filters-title {
    display: flex;
    justify-content: space-between;
}

.previously-logged-activity-widget .filter-dropdowns-container .section-title {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
}

.previously-logged-activity-widget .quick-filters {
    display: flex;
    padding: 0 16px 0 14px;
    margin: 20px 0;
    max-height: 340px;
}

.previously-logged-activity-widget .quick-filters section {
    margin-top: 15px;
    margin-bottom: 0px;
}

.previously-logged-activity-widget .quick-filters::-webkit-scrollbar {
    width: 9px;
    height: 9px;
    box-shadow: none;
}

.previously-logged-activity-widget .quick-filters::-webkit-scrollbar-track {
    border-radius: 32px;
    box-shadow: none;
}

/*Unknown Location*/
.previously-logged-activity-widget .quick-filters::-webkit-scrollbar-thumb {
    border-radius: 32px;
    background: var(--color-background-scrollbar);
    box-shadow: none;
}

.previously-logged-activity-widget .app-search-input {
    width: 231px;
}

.previously-logged-activity-widget .app-search-input .search input {
    background-color: var(--color-fill-accent-brightgray);
}

.previously-logged-activity-widget .available-activities-container {
    border: 2px solid var(--color-border-accent-silvergray);
    border-radius: 12px;
    margin-top: 18px;
}

.previously-logged-activity-widget .activity-container {
    display: flex;
    column-gap: 16px;
    margin: 24px 0;
    padding: 0 14px;
    align-items: flex-start;
}

.previously-logged-activity-widget .activity-container .content-container {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
    flex-grow: 1;
}

.previously-logged-activity-widget .activity-container .content-container .details-container {
    display: flex;
    column-gap: 16px;
}

.previously-logged-activity-widget
    .activity-container
    .content-container
    .details-container
    .date-container {
    text-transform: uppercase;
}

.previously-logged-activity-widget .activity-container .content-container .details-actions {
    display: flex;
    align-items: center;
    column-gap: 12px;
}

.previously-logged-activity-widget
    .activity-container
    .content-container
    .details-actions
    .preview-action-container {
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid var(--color-border-accent-silvergray);
    border-radius: 50%;
}

.previously-logged-activity-widget
    .activity-container
    .content-container
    .details-actions
    .preview-action-container
    svg {
    width: 14px;
}

.previously-logged-activity-widget .image-container {
    position: relative;
    overflow: visible;
    height: 48px;
    width: 48px;
    background-repeat: no-repeat !important;
    background-color: var(--color-fill-accent-white) !important;
    background-size: 100% !important;
    background-position: center !important;
    border-radius: 50%;
    display: inline-block;
    *display: inline;
    zoom: 1;
    flex-shrink: 0;
}

.previously-logged-activity-widget .image-container .icon-image {
    position: absolute;
    bottom: 0;
    right: 0;
    max-width: 24px;
}

.previously-logged-activity-widget .available-activities-container .divider {
    height: 2px;
    margin: 0;
    background-color: var(--color-border-accent-silvergray);
}

.previously-logged-activity-widget .activity-btn-profile {
    display: flex;
    column-gap: 14px;
    align-items: center;
}

/*Unknown Schema Requirement*/
.previously-logged-activity-widget .add-activity-btn-container {
    position: relative;
    overflow: hidden;
    border-radius: 20px;
    background: transparent;
    display: inline-block;
    box-shadow: 0px 3px 12px #187cff44;
    height: 34px;
}

.previously-logged-activity-widget .add-activity-btn-container .add-activity-btn.btn {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--color-fill-accent-skyblue);
    font-family: 'Roboto', sans-serif;
    min-width: 0;
    outline: none;
    border: 2px solid transparent;
    height: 34px;
    width: 34px;
    border-radius: 260px;
    font-size: 12px;
    font-weight: 500;
    line-height: 17px;
    margin: 0px;
    padding: 0;
}

.previously-logged-activity-widget .add-activity-btn-container:hover .btn {
    border: 2px solid var(--color-border-accent-white);
}

.previously-logged-activity-widget .add-activity-btn-container .add-activity-btn.btn:hover {
    background: transparent linear-gradient(180deg, #4bc9fe 0%, #5052fe 100%);
}

.previously-logged-activity-widget .add-activity-btn-container .add-activity-btn.btn .o6-add-svg {
    margin: 0;
}

.previously-logged-activity-widget .outlined-section {
    position: relative;
    border: 1px solid var(--color-border-accent-silvergray);
    border-radius: 16px;
    margin-bottom: 40px;
}

.previously-logged-activity-widget .outlined-section .section-title {
    font: normal normal bold 18px/24px Roboto;
    position: absolute;
    top: -12px;
    left: 0;
    right: 0;
    margin: auto;
    width: fit-content;
    padding: 0px 25px;
    background-color: var(--color-background-secondary);
    font-weight: 600;
}

.previously-logged-activity-widget .outlined-section .outlined-section-content-container {
    overflow: auto;
    max-height: 160px;
}

.previously-logged-activity-widget .outlined-section-content-container .divider {
    height: 1px;
    margin: 0;
    background-color: var(--color-border-accent-silvergray);
}

.previously-logged-activity-widget .details-action-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 4px;
    font-size: 14px;
}

.previously-logged-activity-widget .details-action-container .remove-button {
    background-color: var(--color-fill-accent-vividraspberry);
    display: flex;
    align-items: center;
    padding: 0 20px;
}

/*Unknown Schema Requirement*/
.previously-logged-activity-widget .details-action-container .remove-button:hover {
    background-color: #f5d5e7;
    color: var(--color-text-accent-vividraspberry);
}

.previously-logged-activity-widget .details-action-container .remove-button .s4-delete-svg {
    height: 14px;
    padding-left: 14px;
}

.previously-logged-activity-widget .details-action-container .remove-button .s4-delete-svg .a {
    fill: var(--color-icon-white);
}

.previously-logged-activity-widget
    .details-action-container
    .remove-button:hover
    .s4-delete-svg
    .a {
    fill: var(--color-icon-vividrasbpberry-base);
}

.previously-logged-activity-widget .show-more-btn .app-button {
    margin-top: 24px;
    width: 100%;
}

.previously-logged-activity-widget .no-activities-container {
    width: 100%;
    text-align: center;
    padding: 50px 20px;
}

.previously-logged-activity-widget .no-activities-container h4 {
    margin-top: 23px;
    color: var(--color-text-subtle);
    font-size: 16px;
    font-weight: 400;
}
</style>
